<template>
	<div>
		<v-form
			v-if="!isPollVoted && post.poll.poll_status !== 7"
			:class="{ 'poll-form px-3 mb-3': true, 'poll-form-event': !postId }"
			ref="form"
			v-model="valid"
			lazy-validation
			@submit.prevent="validateAndSubmit"
		>
			<div class="poll-form__title">{{ poll.poll_question }}</div>
			<div
				v-if="postId"
				class="poll-form__answer-new mt-2 u-cursor-pointer"
				:class="{ disabled: post.poll.poll_status === 7 }"
				v-for="answer in poll.pollsAnswers"
				:key="answer.poll_answer_id"
				@click="post.poll.poll_status === 7 ? null : selectAnswer(answer.poll_answer_id)"
			>
				<span class="poll-form__answer-new__title">{{ answer.poll_answer }}</span>
			</div>
		</v-form>
		<v-card
			v-else-if="isPollVoted || post.poll.poll_status === 7"
			:class="{ 'poll-result px-3 elevation-0': true, 'poll-result-event': !postId }"
		>
			<div class="poll-form__title">{{ poll.poll_question }}</div>
			<div v-for="answer in poll.pollsAnswers" :key="answer.poll_answer_id">
				<div class="poll-result__answer-scale">
					<div
						class="poll-result__answer-scale-bar"
						:style="getScaleBarStyle(answer)"
					></div>
					<div class="poll-result__answer-scale__answer">
						{{ answer.is_custom ? 'Custom answer' : answer.poll_answer }}
					</div>
					<div
						:class="{
							'poll-result__answer-scale__percentage': true,
							'poll-result__percentage--bold': isMaxPercentage(answer),
						}"
					>
						{{ Math.round(calcPercentage(answer)) }} %
					</div>
				</div>
			</div>
		</v-card>
		<div class="button-container">
			<span
				:style="{
					color: isMyPost ? 'var(--text_button_tint)' : '#4A4A4A',
					cursor: isMyPost ? 'pointer' : 'default',
				}"
				class="poll-result__answers-count"
				@click="goToPollUsers"
			>
				{{ answersCount }} Votes
			</span>
			<button
				type="button"
				v-if="this.post.poll.poll_status !== 7"
				@click="handleRevote"
				class="poll-result__edit-vote"
			>
				Edit Vote
			</button>
			<span class="poll-result__answers-count">
				{{ pollStatusText }}
			</span>
		</div>
		<div v-if="isMyPost && post.poll.poll_status !== 7" class="button-close-poll mt-3">
			<v-btn type="button" class="poll-form__submit-btn elevation-0" @click="stopPollStatus">
				Close Poll
			</v-btn>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import Dwh from '@/mixins/Dwh'

const AT_LEAST_TYPE = 0
const AT_MOST_TYPE = 1
const EXACTLY_TYPE = 2

export default {
	mixins: [Form, Dwh],
	props: {
		poll: {
			type: Object,
			default: () => {},
		},
		postId: {
			type: Number,
			default: null,
		},
		post: {
			type: Object,
			default: () => {},
		},
		answers: {
			type: Array,
			default: () => [],
		},
		isEventVoteDirty: {
			type: Boolean,
			default: false,
		},
		pollsLoading: {
			type: Boolean,
			default: false,
		},
    typeValue:{
      type: String,
      default: '',
    }
	},
	data() {
		return {
			fields: {
				answer: [],
				customAnswer: '',
			},
			isDirty: false,
		}
	},
	computed: {
		...mapGetters('settings', ['styles']),
		...mapGetters('post', ['isPollVoting']),
		pollStatusText() {
			return this.post.poll.poll_status === 7 ? 'Closed' : 'In Progress'
		},
		isMyPost() {
			return this.post.user.user_id === this.profile.user_id
		},

		isPollVoted() {
			return this.poll.vote
		},
		isSubmitEnabled() {
			return Array.isArray(this.fields.answer)
				? this.fields.answer.length
				: this.fields.answer
		},
		answersCount() {
			return this.poll.pollsAnswers.reduce((acc, item) => acc + item.poll_answer_count, 0)
		},
		customAnswerIsValid() {
			if (Array.isArray(this.fields.answer)) {
				return this.fields.answer.some((item) => item === 'custom')
					? !!this.fields.customAnswer
					: true
			} else {
				return this.fields.answer === 'custom' ? !!this.fields.customAnswer : true
			}
		},
		isCheckboxesValid() {
			if (!Array.isArray(this.fields.answer)) {
				return !!this.fields.answer || 'Answer required'
			} else {
				switch (this.poll.require_answer_option) {
					case AT_LEAST_TYPE: {
						return (
							this.poll.num_of_answers <= this.fields.answer.length ||
							`At least ${this.poll.num_of_answers} variants required`
						)
					}
					case AT_MOST_TYPE: {
						return (
							this.poll.num_of_answers >= this.fields.answer.length ||
							`At most ${this.poll.num_of_answers} variants required`
						)
					}
					case EXACTLY_TYPE: {
						return (
							this.poll.num_of_answers === this.fields.answer.length ||
							`Exactly ${this.poll.num_of_answers} variants required`
						)
					}
					default: {
						return null
					}
				}
			}
		},
		maxPercentage() {
			return Math.max(
				...this.poll.pollsAnswers.map((a) => parseFloat(this.calcPercentage(a).toFixed(1)))
			)
		},
	},
	watch: {
		'fields.answer': {
			handler(newValue) {
				if (!this.postId) {
					const answer = {
						poll_id: this.poll.poll_id,
						answer: newValue,
						error:
							typeof this.isCheckboxesValid === 'string'
								? this.isCheckboxesValid
								: this.customAnswerIsValid
								? null
								: 'Custom answer cannot be empty',
					}
					if (newValue === 'custom') {
						answer.customAnswer = this.fields.customAnswer
					}
					this.$emit('answerChange', answer)
				}
			},
		},
		'fields.customAnswer': {
			handler(newValue) {
				if (!this.postId) {
					this.$emit('answerChange', {
						poll_id: this.poll.poll_id,
						answer: 'custom',
						customAnswer: newValue,
						error: newValue.length ? null : 'Custom answer cannot be empty',
					})
				}
			},
		},
	},
	methods: {
		...mapActions('post', ['votePoll', 'pollStatus']),
    getScaleBarStyle(answer) {
      const width = this.calcPercentage(answer) + '%'
      const customBarColor = 'var(--vote_bar_tint)'
      const defaultBarColor = 'rgba(226, 226, 226, 0.3)'

      const backgroundColor =
          answer.selected === 1 ? customBarColor : defaultBarColor
      return {
        width,
        backgroundColor,
        borderRadius:
            Math.round(this.calcPercentage(answer)) === 100 ? '8px' : '8px 0 0 8px',
      }
    },
		stopPollStatus() {
			if (this.post.poll.poll_status !== 7) {
				this.pollStatus({
					thread_id: this.post.thread_id,
				})
					.then(() => {
						this.$set(this.post.poll, 'poll_status', 7)
					})
					.catch((error) => {
						console.error(error)
					})
			}
		},
		goToPollUsers() {
			if (!this.isMyPost) return
			if (this.post && this.post.thread_id) {
				this.$router.push({
					name: 'polls-users',
					query: {
						thread_id: this.post.thread_id,
					},
				})
			}
		},
		handleRevote() {
			if (!this.poll.vote) return
			this.poll.vote = false
		},
		selectAnswer(answerId) {
			if (!this.pollsLoading) {
				this.fields.answer = answerId
				this.handleSubmit()
			}
		},

		async submitForm() {
			if (!this.isPollVoting) {
				const payload = {
					thread_id: this.post.thread_id,
				}
        if(!this.post.poll.pollsAnswers.some(a => a.selected === 1)){
          payload.vote_from = this.typeValue
        }
				if (this.fields.answer === 'custom') {
					payload.custom_answer = this.fields.customAnswer
				} else {
					payload.poll_answer_id = this.fields.answer
				}
				const isThreads = this.$route.name === 'threads'
				await this.votePoll({ poll: payload, postId: this.postId, isThreads})
			}
		},
		calcPercentage(answer) {
			const percentage = (answer.poll_answer_count / this.answersCount) * 100 || 0
			return parseFloat(percentage.toFixed(1))
		},
		isMaxPercentage(answer) {
			const calcValue = this.calcPercentage(answer)
			const maxValue = this.maxPercentage
			return Math.abs(calcValue - maxValue) < 0.1
		},
		handleSubmit() {
			if (this.isSubmitEnabled) {
				this.isDirty = true
				if (this.customAnswerIsValid && typeof this.isCheckboxesValid !== 'string') {
					this.validateAndSubmit()
					if (['threads', 'posts-by-id'].some((item) => item === this.$route.name)) {
						this.sendTrigger({
							category: 'Content',
							action: 'click_vote_poll_button_news_feed',
							module: 'Polls',
							data: this.poll.poll_id,
							content_chapter_id:
								this.post && this.post.chapter_id ? [this.post.chapter_id] : null,
						})
					}
				}
			}
		},
	},
	mounted() {
		const option = this.poll.require_answer_option
		if (
			this.poll.num_of_answers === 1 &&
			(option === AT_MOST_TYPE || option === EXACTLY_TYPE)
		) {
			this.fields.answer = null
		} else {
			this.fields.answer = []
		}
	},
}
</script>

<style lang="scss">
.button-container {
	display: flex;
	justify-content: space-between;
	padding-left: 12px;
	padding-right: 12px;
}

.poll-form {
	padding-top: 12px !important;
	font-family: Inter, sans-serif;

	&__answer-new {
		padding: 12px;
		display: flex;
		align-items: center;
		font-family: Inter, sans-serif;
		font-weight: 500;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		background-color: #f3f3f6;
		min-height: 44px;
		border-radius: 8px;

		&__title {
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: #002855;
		}
	}

	div {
		width: auto !important;
		max-width: none !important;
	}

	&-event {
		background-color: #f3f3f6;
		border-radius: 10px;
		border: 1px solid #e2e2e2;
		margin-bottom: 12px;
		padding: 16px;
	}

	&__title {
		color: var(--accent-color);
		margin-bottom: 10px !important;
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
	}

	&__answer {
		i {
			font-size: 20px !important;
			color: #e2e2e2 !important;
		}

		label {
			font-size: 14px;
			line-height: 17px;
			color: var(--accent-color) !important;
			word-break: break-word;
		}
	}

	&__custom-answer {
		border-bottom: 1px solid #e2e2e2;

		input {
			padding: 0 !important;
		}

		.v-input__slot::after {
			border-color: var(--accent-color) !important;
		}
	}

	.v-input--is-label-active {
		i {
			color: var(--accent-color) !important;
		}
	}

	&__submit-btn {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px !important;
		line-height: 21.78px;
		font-family: Inter, sans-serif;
		font-weight: 700;
		text-transform: capitalize;
		border: 1.5px solid #0055a3 !important;
		background-color: #ffffff !important;
		color: #0055a3 !important;

		.v-btn__content {
			font-size: 18px !important;
			line-height: 21.78px;
			font-family: Inter, sans-serif;
			font-weight: 700;
		}

		&-disabled {
			opacity: 0.5;
			cursor: default;
		}
	}

	&__error {
		position: absolute;
		top: -8px;
		left: 50%;
		transform: translateX(-50%);
		font-size: 12px;
		color: red;
		margin: 0;
	}
}

.poll-result {
	padding-top: 12px !important;
	font-family: Inter, sans-serif;

	&__answer-scale {
		display: flex;
		align-items: center;
		padding: 12px;
		justify-content: space-between;
		border-radius: 8px;
		min-height: 44px;
		gap: 8px;
		margin-bottom: 8px;
		background-color: #f3f3f6 !important;
		line-height: 100%;
		letter-spacing: 0;

		&__answer {
			font-family: Inter, sans-serif;
			font-weight: 500;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: #002855;
			z-index: 2;
		}

		&__percentage {
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			white-space: nowrap;
			z-index: 2;
		}
	}

	&__percentage--bold {
		font-weight: 700 !important;
	}

	&__edit-vote {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 21.78px;
		color: var(--text_button_tint);
	}

	&__answers-count {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 21.78px;
		color: #4a4a4a;
	}

	&-event {
		padding: 16px;
		background-color: #f3f3f6 !important;
		margin-bottom: 10px;
		border-radius: 10px !important;
		border: 1px solid #e2e2e2 !important;

		.v-progress-linear__buffer {
			background-color: #fff !important;
		}
	}
}

.button-close-poll {
	padding-left: 12px;
	padding-right: 12px;
}

.disabled {
	pointer-events: none;
	opacity: 0.6;
	cursor: not-allowed;
}

.poll-result__answer--selected {
	background-color: red !important;
}

.poll-result__answer-scale-wrapper {
	position: relative;
	margin-bottom: 8px;
	border-radius: 8px;
	overflow: hidden;
}

.poll-result__answer-scale-bar {
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	z-index: 1;
	transition: width 0.3s ease;
}

.poll-result__answer-scale {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px;
	border-radius: 8px;
	min-height: 44px;
	gap: 8px;
}
</style>
